import { StatisticsManager } from './statistics-manager.js';

interface Elements {
  challengeText: HTMLElement;
  statusIndicator: HTMLElement;
  recordingVisual: HTMLElement;
  resultText: HTMLElement;
  timeText: HTMLElement;
  overallAccuracy: HTMLElement;
  totalAttempts: HTMLElement;
  avgTime: HTMLElement;
  worstCombinations: HTMLElement;
  startBtn: HTMLButtonElement;
  stopBtn: HTMLButtonElement;
  resetStatsBtn: HTMLButtonElement;
}

interface ReactCallbacks {
  setStatus?: (state: string, message: string) => void;
  setChallengeText?: (text: string) => void;
  setRecordingVisual?: (active: boolean) => void;
  setButtonsState?: (startDisabled: boolean, stopDisabled: boolean) => void;
  updateStartButtonText?: (isPaused: boolean) => void;
  updateDisplay?: () => void;
  displayResult?: (parsedAnswer: string | null, isCorrect: boolean, responseTime: number, transcription?: string | null, correctAnswer?: string | null, challengeType?: string) => void;
  setChallengeKey?: (key: string) => void;
}

export class UIManager {
  private elements!: Elements;
  private statsManager: StatisticsManager;
  private reactCallbacks?: ReactCallbacks;

  constructor(statsManager: StatisticsManager, reactCallbacks?: ReactCallbacks) {
    this.statsManager = statsManager;
    this.reactCallbacks = reactCallbacks;
    this.initializeElements();
  }

  /**
   * Initialize DOM elements
   */
  private initializeElements(): void {
    this.elements = {
      challengeText: document.getElementById('challenge-text') as HTMLElement,
      statusIndicator: document.getElementById('status-indicator') as HTMLElement,
      recordingVisual: document.getElementById('recording-visual') as HTMLElement,
      resultText: document.getElementById('result-text') as HTMLElement,
      timeText: document.getElementById('time-text') as HTMLElement,
      overallAccuracy: document.getElementById('overall-accuracy') as HTMLElement,
      totalAttempts: document.getElementById('total-attempts') as HTMLElement,
      avgTime: document.getElementById('avg-time') as HTMLElement,
      worstCombinations: document.getElementById('worst-combinations') as HTMLElement,
      startBtn: document.getElementById('start-btn') as HTMLButtonElement,
      stopBtn: document.getElementById('stop-btn') as HTMLButtonElement,
      resetStatsBtn: document.getElementById('reset-stats-btn') as HTMLButtonElement
    };
  }

  /**
   * Get elements for event listeners
   */
  getElements() {
    return this.elements;
  }

  /**
   * Update start button text based on current state
   */
  updateStartButtonText(isPaused: boolean): void {
    if (this.reactCallbacks?.updateStartButtonText) {
      this.reactCallbacks.updateStartButtonText(isPaused);
    } else {
      if (isPaused) {
        this.elements.startBtn.textContent = 'Continue';
      } else {
        this.elements.startBtn.textContent = 'Start';
      }
    }
  }

  /**
   * Update statistics display
   */
  updateDisplay(): void {
    if (this.reactCallbacks?.updateDisplay) {
      this.reactCallbacks.updateDisplay();
    } else {
      const overallStats = this.statsManager.getOverallStats();

      this.elements.overallAccuracy.textContent =
        overallStats.totalAttempts > 0
          ? `${Math.round(overallStats.accuracy * 100)}%`
          : '-%';

      this.elements.totalAttempts.textContent = overallStats.totalAttempts.toString();

      this.elements.avgTime.textContent =
        overallStats.averageTime > 0
          ? `${(overallStats.averageTime / 1000).toFixed(1)}s`
          : '-s';

      // Update worst combinations
      const worstCombinations = this.statsManager.getWorstCombinations(3);
      if (worstCombinations.length > 0) {
        this.elements.worstCombinations.innerHTML = worstCombinations
          .map(combo => {
            const accuracy = Math.round(combo.accuracy * 100);
            const avgTime = (combo.averageTime / 1000).toFixed(1);
            const trendIcon = combo.recentTrend === 'improving' ? '📈' :
                              combo.recentTrend === 'declining' ? '📉' : '➡️';
            return `<div class="problem-item">
              <span class="combo">${combo.degree} of ${combo.key}</span>
              <span class="stats">${accuracy}% (${avgTime}s) ${trendIcon}</span>
            </div>`;
          })
          .join('');
      } else {
        this.elements.worstCombinations.innerHTML =
          '<span class="placeholder">Complete some challenges to see recommendations</span>';
      }
    }
  }

  /**
   * Set application status
   */
  setStatus(state: string, message: string): void {
    if (this.reactCallbacks?.setStatus) {
      this.reactCallbacks.setStatus(state, message);
    } else {
      this.elements.statusIndicator.textContent = message;
      this.elements.statusIndicator.className = `status ${state}`;
    }
  }

  /**
   * Display the result of the user's response with mode-aware feedback
   */
  displayResult(parsedAnswer: string | null, isCorrect: boolean, responseTime: number, transcription: string | null = null, correctAnswer: string | null = null, challengeType?: string): void {
    if (this.reactCallbacks?.displayResult) {
      this.reactCallbacks.displayResult(parsedAnswer, isCorrect, responseTime, transcription, correctAnswer);
    } else {
      const resultIcon = isCorrect ? '✓' : '✗';
      const resultClass = isCorrect ? 'correct' : 'incorrect';

      // Show both transcription and parsed answer for debugging
      let displayText = parsedAnswer || 'Unknown';
      if (transcription && transcription !== parsedAnswer) {
        displayText += ` (heard: "${transcription}")`;
      }

      // If incorrect, show what the correct answer should have been with mode-specific context
      if (!isCorrect && correctAnswer) {
        const answerType = challengeType === 'inverse' ? 'key' : 'note';
        displayText += ` (correct ${answerType}: ${correctAnswer})`;
      }

      this.elements.resultText.textContent = `${displayText} → ${resultIcon}`;
      this.elements.resultText.className = `result ${resultClass}`;

      this.elements.timeText.textContent = `${(responseTime / 1000).toFixed(1)}s`;

      // Mode-specific feedback messages
      let feedbackMessage = isCorrect ? 'Correct' : 'Incorrect';
      if (!isCorrect && challengeType) {
        if (challengeType === 'inverse') {
          feedbackMessage = 'Incorrect key';
        } else {
          feedbackMessage = 'Incorrect note';
        }
      }

      this.setStatus('result', `${feedbackMessage} - Next challenge coming...`);
    }
  }

  /**
   * Set challenge text
   */
  setChallengeText(text: string): void {
    if (this.reactCallbacks?.setChallengeText) {
      this.reactCallbacks.setChallengeText(text);
    } else {
      this.elements.challengeText.textContent = text;
    }
  }

  /**
   * Set recording visual active state
   */
  setRecordingVisual(active: boolean): void {
    if (this.reactCallbacks?.setRecordingVisual) {
      this.reactCallbacks.setRecordingVisual(active);
    } else {
      if (active) {
        this.elements.recordingVisual.classList.add('active');
      } else {
        this.elements.recordingVisual.classList.remove('active');
      }
    }
  }

  /**
   * Enable/disable buttons
   */
  setButtonsState(startDisabled: boolean, stopDisabled: boolean): void {
    if (this.reactCallbacks?.setButtonsState) {
      this.reactCallbacks.setButtonsState(startDisabled, stopDisabled);
    } else {
      this.elements.startBtn.disabled = startDisabled;
      this.elements.stopBtn.disabled = stopDisabled;
    }
  }
}